import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import {
  Select,
  SelectContent,
  SelectItem as SelectItemUi,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  centsToInputDollars,
  centsToInputUnitDollars,
  formatCurrency,
  formatUnitCurrency,
  inputDollarsToCents,
  inputDollarsToUnitCents,
} from '@/lib/formatters';
import { cn } from '@/lib/utils';

/**
 * Inline-edit primitives for the item master.
 *
 * The item list grid and the 360 detail page both render a *lot* of
 * scalar fields, so we keep the click-to-edit machinery centralised
 * here and ship four field types tuned for the columns operators
 * actually edit:
 *
 *   - `EditableText`     SKU, name, base UoM, free text
 *   - `EditableCurrency` standard cost, retail price (cents in / out)
 *   - `EditableSelect`   status, kind (any string-enum + nice display)
 *   - `EditableCategory` text input with a datalist of existing values
 *
 * UX:
 *   - The trigger looks like the rendered value but lights up on hover
 *     when the user can edit, with a small pencil hint.
 *   - Click → the cell swaps to an input/select with the current value
 *     pre-filled and selected.
 *   - Enter / blur commits, Esc reverts. The Save button is the same
 *     "commit" path so the keyboard and mouse stay in sync.
 *   - Errors thrown by `onSave` (validation, RLS, unique violation)
 *     show as a toast and the editor stays open so the operator can
 *     correct the value without losing their place.
 *
 * The `density` prop lets the same component fit either a tight table
 * cell ("cell") or the roomier KV row on the detail page ("kv").
 */

type Density = 'cell' | 'kv';
type Align = 'left' | 'right';

interface BaseProps {
  canEdit: boolean;
  ariaLabel: string;
  density?: Density;
  align?: Align;
  /**
   * Render override for the static (non-editing) display. Lets the
   * Status field show a Badge while still using the same inline-edit
   * shell. Defaults to the formatted scalar value.
   */
  display?: ReactNode;
  /** Extra classes for the trigger element (the read-only chrome). */
  triggerClassName?: string;
  /** Extra classes for the input/textarea/select trigger. */
  inputClassName?: string;
}

// =====================================================================
// EditableText
// =====================================================================

interface EditableTextProps extends BaseProps {
  value: string;
  onSave: (next: string) => Promise<void> | void;
  /**
   * "mono" renders with the monospace font + smaller text we use for
   * SKU/identifier columns; "plain" is the default body text.
   */
  variant?: 'plain' | 'mono';
  /** Reject empty input. SKU + Name use this. */
  required?: boolean;
  multiline?: boolean;
  /**
   * Visible row count for the textarea when `multiline` is on.
   * Defaults to 3 to match the legacy compact editor; bump for
   * longer-form fields like product descriptions.
   */
  rows?: number;
  placeholder?: string;
  maxLength?: number;
}

export function EditableText({
  value,
  onSave,
  canEdit,
  ariaLabel,
  density = 'cell',
  align = 'left',
  display,
  triggerClassName,
  inputClassName,
  variant = 'plain',
  required = false,
  multiline = false,
  rows = 3,
  placeholder,
  maxLength,
}: EditableTextProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  // Focus + select all on enter so the operator can immediately
  // overwrite or extend the existing value.
  useEffect(() => {
    if (editing) {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.select();
      }
    }
  }, [editing]);

  if (editing) {
    const commit = async () => {
      const next = draft;
      const trimmed = next.trim();
      if (required && !trimmed) {
        toast.error(`${ariaLabel} can't be empty.`);
        return;
      }
      if (next === value) {
        setEditing(false);
        return;
      }
      try {
        setPending(true);
        await onSave(next);
        setEditing(false);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : `Couldn't save ${ariaLabel}.`);
      } finally {
        setPending(false);
      }
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDraft(value);
        setEditing(false);
        return;
      }
      // Enter commits; Shift+Enter is a newline in textarea mode.
      if (e.key === 'Enter' && !(multiline && e.shiftKey)) {
        e.preventDefault();
        void commit();
      }
    };

    return (
      <EditingShell
        density={density}
        onContainerClick={(e) => e.stopPropagation()}
        onCommit={() => void commit()}
        onCancel={() => {
          setDraft(value);
          setEditing(false);
        }}
        pending={pending}
      >
        {multiline ? (
          <Textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            maxLength={maxLength}
            aria-label={ariaLabel}
            disabled={pending}
            rows={rows}
            className={cn('resize-y', inputClassName)}
          />
        ) : (
          <Input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            maxLength={maxLength}
            aria-label={ariaLabel}
            disabled={pending}
            className={cn(
              density === 'cell' && 'h-8',
              variant === 'mono' && 'font-mono text-xs',
              align === 'right' && 'text-right',
              inputClassName,
            )}
          />
        )}
      </EditingShell>
    );
  }

  return (
    <DisplayShell
      canEdit={canEdit}
      ariaLabel={ariaLabel}
      onStart={() => setEditing(true)}
      density={density}
      align={align}
      triggerClassName={triggerClassName}
      multiline={multiline}
    >
      {display ?? (
        <span
          className={cn(
            variant === 'mono' && 'font-mono text-xs',
            !value && 'text-muted-foreground',
          )}
        >
          {value || placeholder || '—'}
        </span>
      )}
    </DisplayShell>
  );
}

// =====================================================================
// EditableCurrency
// =====================================================================

interface EditableCurrencyProps extends BaseProps {
  /** Cents — the canonical storage unit. Pass `null` for "unset". */
  value: number | null;
  onSave: (nextCents: number | null) => Promise<void> | void;
  placeholder?: string;
  /** Unit costs: keep sub-cent amounts ($0.00125) instead of rounding to the cent. */
  precise?: boolean;
}

export function EditableCurrency({
  value,
  onSave,
  canEdit,
  ariaLabel,
  density = 'cell',
  align = 'right',
  display,
  triggerClassName,
  inputClassName,
  placeholder = '0.00',
  precise = false,
}: EditableCurrencyProps) {
  const toInput = precise ? centsToInputUnitDollars : centsToInputDollars;
  const parse = precise ? inputDollarsToUnitCents : inputDollarsToCents;
  const format = precise ? formatUnitCurrency : formatCurrency;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(toInput(value));
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(toInput(value));
  }, [value, editing, toInput]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  if (editing) {
    const commit = async () => {
      let nextCents: number | null;
      try {
        nextCents = parse(draft);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Invalid amount.');
        return;
      }
      if (nextCents === value) {
        setEditing(false);
        return;
      }
      try {
        setPending(true);
        await onSave(nextCents);
        setEditing(false);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : `Couldn't save ${ariaLabel}.`);
      } finally {
        setPending(false);
      }
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDraft(toInput(value));
        setEditing(false);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        void commit();
      }
    };

    return (
      <EditingShell
        density={density}
        onContainerClick={(e) => e.stopPropagation()}
        onCommit={() => void commit()}
        onCancel={() => {
          setDraft(toInput(value));
          setEditing(false);
        }}
        pending={pending}
      >
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          inputMode="decimal"
          aria-label={ariaLabel}
          disabled={pending}
          className={cn(
            'tabular-nums',
            density === 'cell' && 'h-8',
            align === 'right' && 'text-right',
            inputClassName,
          )}
        />
      </EditingShell>
    );
  }

  return (
    <DisplayShell
      canEdit={canEdit}
      ariaLabel={ariaLabel}
      onStart={() => setEditing(true)}
      density={density}
      align={align}
      triggerClassName={cn('tabular-nums', triggerClassName)}
    >
      {display ?? <span>{format(value)}</span>}
    </DisplayShell>
  );
}

// =====================================================================
// EditableNumber — integer values stored as `number | null`. Used for
// reorder point / qty etc. Numeric input only; clearing the field
// nulls the column.
// =====================================================================

interface EditableNumberProps extends BaseProps {
  value: number | null;
  onSave: (next: number | null) => Promise<void> | void;
  /** Smallest accepted integer; defaults to 0 for inventory thresholds. */
  min?: number;
  placeholder?: string;
}

export function EditableNumber({
  value,
  onSave,
  canEdit,
  ariaLabel,
  density = 'cell',
  align = 'right',
  display,
  triggerClassName,
  inputClassName,
  min = 0,
  placeholder = '—',
}: EditableNumberProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value == null ? '' : String(value));
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value == null ? '' : String(value));
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  if (editing) {
    const commit = async () => {
      const trimmed = draft.trim();
      let next: number | null;
      if (!trimmed) {
        next = null;
      } else {
        const parsed = Number(trimmed);
        if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < min) {
          toast.error(
            `${ariaLabel} must be a whole number${min === 0 ? ' (≥ 0)' : ` (≥ ${min})`}.`,
          );
          return;
        }
        next = parsed;
      }
      if (next === (value ?? null)) {
        setEditing(false);
        return;
      }
      try {
        setPending(true);
        await onSave(next);
        setEditing(false);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : `Couldn't save ${ariaLabel}.`);
      } finally {
        setPending(false);
      }
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDraft(value == null ? '' : String(value));
        setEditing(false);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        void commit();
      }
    };

    return (
      <EditingShell
        density={density}
        onContainerClick={(e) => e.stopPropagation()}
        onCommit={() => void commit()}
        onCancel={() => {
          setDraft(value == null ? '' : String(value));
          setEditing(false);
        }}
        pending={pending}
      >
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          inputMode="numeric"
          aria-label={ariaLabel}
          disabled={pending}
          className={cn(
            'tabular-nums',
            density === 'cell' && 'h-8',
            align === 'right' && 'text-right',
            inputClassName,
          )}
        />
      </EditingShell>
    );
  }

  return (
    <DisplayShell
      canEdit={canEdit}
      ariaLabel={ariaLabel}
      onStart={() => setEditing(true)}
      density={density}
      align={align}
      triggerClassName={cn('tabular-nums', triggerClassName)}
    >
      {display ?? <span>{value == null ? '—' : value.toLocaleString()}</span>}
    </DisplayShell>
  );
}

// =====================================================================
// EditableBool — Yes / No select for boolean columns (e.g. is_lot_tracked).
// =====================================================================

interface EditableBoolProps extends BaseProps {
  value: boolean;
  onSave: (next: boolean) => Promise<void> | void;
  trueLabel?: string;
  falseLabel?: string;
}

export function EditableBool({
  value,
  onSave,
  canEdit,
  ariaLabel,
  density = 'kv',
  align = 'left',
  display,
  triggerClassName,
  inputClassName,
  trueLabel = 'Yes',
  falseLabel = 'No',
}: EditableBoolProps) {
  return (
    <EditableSelect<'yes' | 'no'>
      value={value ? 'yes' : 'no'}
      options={[
        { value: 'yes', label: trueLabel },
        { value: 'no', label: falseLabel },
      ]}
      ariaLabel={ariaLabel}
      canEdit={canEdit}
      density={density}
      align={align}
      display={display ?? <span>{value ? trueLabel : falseLabel}</span>}
      triggerClassName={triggerClassName}
      inputClassName={inputClassName}
      onSave={async (next) => {
        await onSave(next === 'yes');
      }}
    />
  );
}

// =====================================================================
// EditableSelect
// =====================================================================

interface EditableSelectOption<T extends string> {
  value: T;
  label: string;
  /** Optional secondary text rendered under the label inside the popover. */
  hint?: string;
}

interface EditableSelectProps<T extends string> extends BaseProps {
  value: T;
  options: ReadonlyArray<EditableSelectOption<T>>;
  onSave: (next: T) => Promise<void> | void;
}

export function EditableSelect<T extends string>({
  value,
  options,
  onSave,
  canEdit,
  ariaLabel,
  density = 'cell',
  align = 'left',
  display,
  triggerClassName,
  inputClassName,
}: EditableSelectProps<T>) {
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  // Ref shadow of `pending` so the Radix `onOpenChange` callback (which
  // fires synchronously with the user's click on a popover item, before
  // React has flushed the `setPending(true)` from `commit`) can see the
  // in-flight state and avoid prematurely tearing down the editor.
  const pendingRef = useRef(false);

  const enter = () => {
    setEditing(true);
    setOpen(true);
  };

  const exit = () => {
    setOpen(false);
    setEditing(false);
  };

  const commit = async (next: T) => {
    if (next === value) {
      exit();
      return;
    }
    try {
      pendingRef.current = true;
      await onSave(next);
      exit();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `Couldn't save ${ariaLabel}.`);
      // Re-open the menu so the operator can immediately retry without
      // having to re-click into the cell.
      setOpen(true);
      setEditing(true);
    } finally {
      pendingRef.current = false;
    }
  };

  if (editing) {
    return (
      <div
        className={cn(
          'flex items-center gap-1.5',
          align === 'right' && 'justify-end',
        )}
        onClick={(e) => e.stopPropagation()}
        onMouseDownCapture={(e) => e.stopPropagation()}
      >
        <Select
          value={value}
          onValueChange={(v) => void commit(v as T)}
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            // Only collapse the whole editor when the user dismisses
            // the menu (click-outside / Esc) — never while a save is
            // mid-flight, otherwise the editor disappears under them.
            if (!next && !pendingRef.current) {
              setEditing(false);
            }
          }}
        >
          <SelectTrigger
            aria-label={ariaLabel}
            className={cn(density === 'cell' && 'h-8', inputClassName)}
          >
            {/* Reuse the static display (e.g. status Badge) inside the
                trigger so the editing state keeps the same visual
                language as the read-out — no jarring switch from a
                colored pill to plain text just because the menu is
                open. Falls back to the default value text otherwise. */}
            {display ?? <SelectValue />}
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItemUi key={opt.value} value={opt.value}>
                {opt.hint ? (
                  <div className="flex flex-col">
                    <span>{opt.label}</span>
                    <span className="text-xs text-muted-foreground">{opt.hint}</span>
                  </div>
                ) : (
                  opt.label
                )}
              </SelectItemUi>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  return (
    <DisplayShell
      canEdit={canEdit}
      ariaLabel={ariaLabel}
      onStart={enter}
      density={density}
      align={align}
      triggerClassName={triggerClassName}
    >
      {display ?? (
        <span>{options.find((o) => o.value === value)?.label ?? value}</span>
      )}
    </DisplayShell>
  );
}

// =====================================================================
// EditableCategory
//
// A free-text editor backed by a `<datalist>` of existing categories so
// operators can keep the bucket list small without forcing them to pick
// from a hard-coded set. Stored as `string | null`; clearing the input
// nulls the column.
// =====================================================================

interface EditableCategoryProps extends BaseProps {
  value: string | null;
  onSave: (next: string | null) => Promise<void> | void;
  options: ReadonlyArray<string>;
  placeholder?: string;
}

export function EditableCategory({
  value,
  onSave,
  options,
  canEdit,
  ariaLabel,
  density = 'cell',
  align = 'left',
  display,
  triggerClassName,
  inputClassName,
  placeholder = 'No category',
}: EditableCategoryProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Stable id so multiple cells on the same page don't trip over each
  // other's datalists. `useId` is the React-blessed way but a stable
  // ref-counter is fine here too — datalists don't need to render in
  // SSR-deterministic order.
  const datalistId = useRef(`cat-options-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!editing) setDraft(value ?? '');
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  if (editing) {
    const commit = async () => {
      const trimmed = draft.trim();
      const next = trimmed === '' ? null : trimmed;
      if (next === (value ?? null)) {
        setEditing(false);
        return;
      }
      try {
        setPending(true);
        await onSave(next);
        setEditing(false);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : `Couldn't save ${ariaLabel}.`);
      } finally {
        setPending(false);
      }
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setDraft(value ?? '');
        setEditing(false);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        void commit();
      }
    };

    return (
      <EditingShell
        density={density}
        onContainerClick={(e) => e.stopPropagation()}
        onCommit={() => void commit()}
        onCancel={() => {
          setDraft(value ?? '');
          setEditing(false);
        }}
        pending={pending}
      >
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          aria-label={ariaLabel}
          disabled={pending}
          list={datalistId.current}
          className={cn(density === 'cell' && 'h-8', inputClassName)}
        />
        {options.length > 0 && (
          <datalist id={datalistId.current}>
            {options.map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>
        )}
      </EditingShell>
    );
  }

  return (
    <DisplayShell
      canEdit={canEdit}
      ariaLabel={ariaLabel}
      onStart={() => setEditing(true)}
      density={density}
      align={align}
      triggerClassName={triggerClassName}
    >
      {display ??
        (value ? (
          <Badge variant="outline">{value}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ))}
    </DisplayShell>
  );
}

// =====================================================================
// Internal — display + editing shells
// =====================================================================

function DisplayShell({
  canEdit,
  ariaLabel,
  onStart,
  density,
  align,
  triggerClassName,
  multiline = false,
  children,
}: {
  canEdit: boolean;
  ariaLabel: string;
  onStart: () => void;
  density: Density;
  align: Align;
  triggerClassName?: string;
  /**
   * When true, the displayed content wraps across multiple lines
   * instead of truncating with an ellipsis. Used by long-form text
   * fields like the item description so the full value is always
   * visible without clicking into edit mode.
   */
  multiline?: boolean;
  children: ReactNode;
}) {
  if (!canEdit) {
    return (
      <span
        className={cn(
          multiline
            ? 'block w-full whitespace-pre-wrap break-words'
            : 'inline-flex items-center',
          align === 'right' && (multiline ? 'text-right' : 'justify-end'),
          density === 'kv' && 'min-h-[24px]',
          triggerClassName,
        )}
      >
        {children}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        // The Items grid's row-level navigate handler is registered on
        // the table row, so any click that should switch to edit mode
        // has to swallow the event before it bubbles up.
        e.stopPropagation();
        onStart();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onStart();
        }
      }}
      aria-label={`Edit ${ariaLabel}`}
      title={`Edit ${ariaLabel}`}
      className={cn(
        // Visual: looks like inline text by default, gains a soft
        // border + bg on hover so the operator can see what's
        // editable. Pencil icon hints at the action.
        'group inline-flex w-full gap-1.5 rounded-md border border-transparent px-1.5 py-0.5 text-left text-foreground transition-colors',
        // Multi-line content top-aligns its pencil hint so it doesn't
        // float at the vertical centre of a tall block of text.
        multiline ? 'items-start' : 'items-center',
        'hover:border-border hover:bg-secondary/60 focus-visible:border-ring focus-visible:bg-secondary/60 focus-visible:outline-none',
        align === 'right' && 'justify-end',
        density === 'cell' ? '-mx-1.5 -my-0.5' : 'min-h-[28px]',
        triggerClassName,
      )}
    >
      <span
        className={cn(
          'flex-1',
          multiline ? 'whitespace-pre-wrap break-words' : 'truncate',
          align === 'right' && 'text-right',
        )}
      >
        {children}
      </span>
      <Pencil
        className={cn(
          'h-3 w-3 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100',
          multiline && 'mt-1',
        )}
        aria-hidden
      />
    </button>
  );
}

function EditingShell({
  density,
  onContainerClick,
  onCommit,
  onCancel,
  pending,
  children,
}: {
  density: Density;
  onContainerClick: (e: React.MouseEvent) => void;
  onCommit: () => void;
  onCancel: () => void;
  pending: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className="flex items-center gap-1"
      // Bubble-phase listener: lets the Save/Cancel buttons' own
      // onClick fire first (they need to run `onCommit`/`onCancel`),
      // then we stop the event so it never reaches the row-click
      // navigate handler on the Items grid. Doing this in the *capture*
      // phase would kill the synthetic event before the buttons see it
      // — that's why earlier versions had non-functional Check/X.
      onClick={onContainerClick}
      // mousedown has no listeners on the buttons themselves, so it's
      // safe (and slightly cheaper) to stop it in the capture phase
      // before the row sees it.
      onMouseDownCapture={(e) => e.stopPropagation()}
    >
      <div className="flex-1">{children}</div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onCommit();
        }}
        disabled={pending}
        aria-label="Save"
        title="Save"
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-md border border-border bg-card text-foreground transition-colors hover:bg-secondary',
          'disabled:cursor-not-allowed disabled:opacity-50',
          density === 'cell' ? 'h-8 w-8' : 'h-9 w-9',
        )}
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onCancel();
        }}
        disabled={pending}
        aria-label="Cancel"
        title="Cancel"
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground',
          'disabled:cursor-not-allowed disabled:opacity-50',
          density === 'cell' ? 'h-8 w-8' : 'h-9 w-9',
        )}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
